import { useTranslation } from "react-i18next";
import type { FunctionComponent } from "@/common/types/types";
import { Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { SignInSchema, type SignInSchemaType } from "@/schemas/signIn.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthStore } from "@/store/auth.store";
import { useEffect } from "react";
import TwoFactorForm from "./components/TwoFactorForm";
import useAuth2FA from "@/hooks/user/useAuth2FA";
import { AUTH_ERRORS } from "@/common/errors";
import { SignInForm } from "./components/SignInForm";
import { SignHeader } from "@/components/ui/SignHeader";
import { NewVerifyEmailLinToast } from "./components/NewVerifyEmailLinkToast";
import { AuthStatus } from "@/common/enums/enums";
import { LanguageSelector } from "@/components/ui/LanguageSelector";
import { GoogleSignInButton } from "@/components/ui/GoogleSignInButton";

export const Login = (): FunctionComponent => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn, signInWithGoogle, loading, error, authStatus } =
    useAuthStore();
  const {
    requestSMSCode,
    handleEnterCode,
    loading: loadingTwoFactor,
    error: errorTwoFactor,
    showTwoFactorForm,
  } = useAuth2FA();

  const form = useForm<SignInSchemaType>({
    resolver: zodResolver(SignInSchema),
    shouldFocusError: true,
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (error == AUTH_ERRORS.MULTI_FACTOR_REQUIRED) {
      void requestSMSCode(form.getValues().email, form.getValues().password);
    }
    if (
      error == AUTH_ERRORS.INVALID_CREDENTIALS ||
      error == AUTH_ERRORS.USER_NOT_FOUND ||
      error == AUTH_ERRORS.WRONG_PASSWORD
    )
      form.setError("root", {
        type: "manual",
        message: t("login.form.messages.invalidCredentials"),
      });
    if (error == AUTH_ERRORS.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL) {
      form.setError("root", {
        type: "manual",
        message: t("login.form.messages.googleAccountExists"),
      });
    }
    if (
      error == AUTH_ERRORS.POPUP_CLOSED ||
      error === "auth/popup-closed-by-user"
    ) {
      form.clearErrors("root");
    }
    if (
      error &&
      error !== AUTH_ERRORS.MULTI_FACTOR_REQUIRED &&
      error !== AUTH_ERRORS.EMAIL_NOT_VERIFIED &&
      error !== AUTH_ERRORS.POPUP_CLOSED &&
      error !== "auth/popup-closed-by-user" &&
      error !== AUTH_ERRORS.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL &&
      error !== AUTH_ERRORS.INVALID_CREDENTIALS &&
      error !== AUTH_ERRORS.USER_NOT_FOUND &&
      error !== AUTH_ERRORS.WRONG_PASSWORD
    ) {
      form.setError("root", {
        type: "manual",
        message: t("login.form.messages.googleSignInFailed"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const onSubmit = async (values: SignInSchemaType): Promise<void> => {
    try {
      await signIn(values.email, values.password);
    } catch {
      // soft catch
    }
  };

  const onGoogleSignIn = async (): Promise<void> => {
    form.clearErrors("root");
    try {
      await signInWithGoogle();
    } catch {
      // handled in store
    }
  };

  useEffect(() => {
    if (authStatus === AuthStatus.AUTHENTICATED) {
      void navigate({ to: "/visits" });
    }
  }, [authStatus, navigate]);

  if (showTwoFactorForm) {
    return (
      <TwoFactorForm
        error={errorTwoFactor}
        loading={loadingTwoFactor}
        onSubmit={handleEnterCode}
      />
    );
  }

  return (
    <div className="w-full flex flex-col justify-center items-center">
      <div className="w-full flex flex-col max-w-[450px] px-4 gap-4">
        <SignHeader title={t("login.title")} />
        {error == AUTH_ERRORS.EMAIL_NOT_VERIFIED && <NewVerifyEmailLinToast />}
        <SignInForm form={form} isSubmitting={loading} onSubmit={onSubmit} />
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground uppercase">
            {t("auth.orContinueWith")}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <GoogleSignInButton
          disabled={loading}
          loading={loading}
          onClick={() => {
            void onGoogleSignIn();
          }}
        />
        <Link
          className="text-tinted-grey-4 text-center font-normal text-base"
          to={"/sign-up"}
        >
          {t("login.noAccount")}{" "}
          <span className="text-primary cursor-pointer">
            {t("login.signUpLink")}
          </span>
        </Link>
      </div>
      <div className="fixed top-4 right-4 z-50 ">
        <LanguageSelector />
      </div>
    </div>
  );
};
